// src/padplanner/usePlanImagery.js
//
// The imagery a plan's map opens on, for the plan page and the editor alike:
// the same lanes (GET /plans/:id/epochs, listed only once there is a
// perimeter) and the same choice (planVocabulary's openingCapture: the pin,
// or with none the newest collect). The editor once drew Mapbox alone for an
// unpinned plan while the plan page showed the newest collect (DB,
// 2026-10-29); both now read this hook, so they cannot disagree.
//
// usePlanLanes: {lanes, error, retry}. lanes is {collects, reference,
// imagery_entitled}, or null with no perimeter or until the read answers; a
// failed read is `error` (said by the page with a Retry), not an empty strip.
// openingImagery: {key, pinned, capture}. capture is the collect shown when
// not pinned (its acquisition_date is the notice's date), else null.
// notPinnedText / notPinnedEmptyText: the editor's notice for an unpinned
// plan, with the newest capture's date or, with none, why there is none;
// notPinnedShowingText once a chip on the editor's strip is picked.

import { useCallback, useEffect, useState } from "react";
import { getEpochs } from "../api/padplannerApi";
import { chipDate, imageryEmptyText, openingCapture } from "./planVocabulary";

export function usePlanLanes(planId, perimeter) {
  const [state, setState] = useState({ lanes: null, error: null });
  const [attempt, setAttempt] = useState(0);
  const hasPerimeter = Boolean(perimeter);
  useEffect(() => {
    if (!hasPerimeter) return undefined;
    let cancelled = false;
    getEpochs(planId)
      .then((r) => { if (!cancelled) setState({ lanes: { collects: r.collects, reference: r.reference, imagery_entitled: r.imagery_entitled }, error: null }); })
      .catch((e) => { if (!cancelled) setState((s) => ({ lanes: s.lanes, error: e })); });
    return () => { cancelled = true; };
    // The point the lanes are listed at moves only when the perimeter does.
  }, [planId, hasPerimeter, perimeter, attempt]);
  const retry = useCallback(() => { setState((s) => ({ ...s, error: null })); setAttempt((n) => n + 1); }, []);
  return hasPerimeter ? { lanes: state.lanes, error: state.error, retry } : { lanes: null, error: null, retry };
}

export function openingImagery(pinnedKey, lanes) {
  const key = openingCapture(pinnedKey, lanes);
  if (!key || pinnedKey) return { key, pinned: Boolean(pinnedKey), capture: null };
  return { key, pinned: false, capture: lanes?.collects?.find((c) => c.dataset_key === key) ?? null };
}

export const notPinnedText = (capture) => `Not pinned — showing newest capture (${chipDate(capture)})`;
export const notPinnedEmptyText = (imageryEntitled) => `Not pinned — ${imageryEmptyText(imageryEntitled)} Showing the basemap.`;
// A chip picked on the editor's strip (S-e slice 3): the capture it shows, by its chip's words.
export const notPinnedShowingText = (label) => `Not pinned — showing ${label}`;
